import React, { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { inr, fmtDate, CYLINDERS, cylShort } from "@/lib/format";
import { DateField } from "@/components/DateField";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Pencil, Trash2, RotateCcw, History, ClipboardList } from "lucide-react";

const TYPE_LABEL = { consumer_delivery: "Consumer delivery", nonconsumer_sale: "Non-consumer sale" };
const todayIso = () => new Date().toISOString().slice(0, 10);

export default function Transactions() {
  const [date, setDate] = useState(todayIso());
  const [driverId, setDriverId] = useState("all");
  const [type, setType] = useState("all");
  const [drivers, setDrivers] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [editRow, setEditRow] = useState(null);
  const [form, setForm] = useState({});
  const [reason, setReason] = useState("");
  const [delRow, setDelRow] = useState(null);
  const [delReason, setDelReason] = useState("");
  const [histRow, setHistRow] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/drivers").then((r) => setDrivers(r.data)); }, []);

  const driverName = useCallback((id) => (drivers.find((d) => d.id === id) || {}).name || "—", [drivers]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { include_voided: true };
      if (date) params.date = date;
      if (driverId !== "all") params.driver_id = driverId;
      if (type !== "all") params.type = type;
      const r = await api.get("/transactions", { params });
      setRows(r.data);
    } finally { setLoading(false); }
  }, [date, driverId, type]);
  useEffect(() => { load(); }, [load]);

  const openEdit = (t) => {
    setEditRow(t);
    setForm({
      consumer_no: t.consumer_no || "", customer_name: t.customer_name || "",
      customer_phone: t.customer_phone || "", cylinder_type: t.cylinder_type,
      qty: t.qty, amount: t.amount, payment_mode: t.payment_mode,
      empties_collected: t.empties_collected || 0,
    });
    setReason("");
  };

  const saveEdit = async () => {
    if (!reason.trim()) { toast.error("Please enter a reason for the change"); return; }
    setBusy(true);
    try {
      await api.put(`/transactions/${editRow.id}`, {
        ...form, amount: parseFloat(form.amount), qty: parseInt(form.qty, 10),
        empties_collected: parseInt(form.empties_collected, 10), reason: reason.trim(),
      });
      toast.success("Transaction updated");
      setEditRow(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const doVoid = async () => {
    if (!delReason.trim()) { toast.error("Please enter a reason"); return; }
    setBusy(true);
    try {
      await api.post(`/transactions/${delRow.id}/void`, { reason: delReason.trim() });
      toast.success("Transaction deleted (kept in audit log)");
      setDelRow(null); setDelReason(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const doUnvoid = async (t) => {
    await api.post(`/transactions/${t.id}/unvoid`);
    toast.success("Transaction restored");
    load();
  };

  return (
    <div className="space-y-6 fade-up" data-testid="transactions-page">
      <div>
        <h1 className="font-head text-2xl font-bold flex items-center gap-2"><ClipboardList className="h-6 w-6 text-primary" /> Transactions</h1>
        <p className="text-sm text-muted-foreground">Review, correct or delete deliveries and non-consumer sales. Every change is kept in an audit trail.</p>
      </div>

      <Card className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="flex flex-col gap-1.5"><Label>Date</Label><DateField value={date} onChange={setDate} testid="txn-date-filter" className="w-full" /></div>
          <div className="flex flex-col gap-1.5"><Label>Driver</Label>
            <Select value={driverId} onValueChange={setDriverId}>
              <SelectTrigger data-testid="txn-driver-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All drivers</SelectItem>
                {drivers.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex flex-col gap-1.5"><Label>Type</Label>
            <Select value={type} onValueChange={setType}>
              <SelectTrigger data-testid="txn-type-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All types</SelectItem>
                <SelectItem value="consumer_delivery">Consumer delivery</SelectItem>
                <SelectItem value="nonconsumer_sale">Non-consumer sale</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </Card>

      <Card className="p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <Table data-testid="transactions-table">
            <TableHeader>
              <TableRow>
                <TableHead>Driver</TableHead><TableHead>Type</TableHead><TableHead>Customer / Consumer</TableHead>
                <TableHead>Cylinder</TableHead><TableHead className="text-right">Qty</TableHead>
                <TableHead className="text-right">Amount</TableHead><TableHead>Mode</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Loading…</TableCell></TableRow>}
              {!loading && rows.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No transactions for these filters.</TableCell></TableRow>}
              {!loading && rows.map((t) => (
                <TableRow key={t.id} className={t.voided ? "opacity-60" : ""} data-testid={`txn-row-${t.id}`}>
                  <TableCell className="font-medium">{driverName(t.driver_id)}</TableCell>
                  <TableCell>{TYPE_LABEL[t.type] || t.type}{t.voided && <Badge variant="outline" className="ml-2 border-rose-300 text-rose-700">Deleted</Badge>}</TableCell>
                  <TableCell className="max-w-[180px] truncate">{t.type === "consumer_delivery" ? t.consumer_no : (t.customer_name || "—")}</TableCell>
                  <TableCell>{cylShort(t.cylinder_type)}</TableCell>
                  <TableCell className="text-right tabular-nums">{t.qty}</TableCell>
                  <TableCell className={`text-right tabular-nums ${t.voided ? "line-through" : ""}`}>{inr(t.amount)}</TableCell>
                  <TableCell className="uppercase text-xs">{t.payment_mode}</TableCell>
                  <TableCell className="text-right whitespace-nowrap space-x-1">
                    {(t.history?.length || 0) > 0 && (
                      <Button size="sm" variant="ghost" className="px-2" onClick={() => setHistRow(t)} data-testid={`txn-history-${t.id}`}><History className="h-4 w-4" /></Button>
                    )}
                    {!t.voided ? (
                      <>
                        <Button size="sm" variant="outline" className="px-2" onClick={() => openEdit(t)} data-testid={`txn-edit-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="sm" variant="outline" className="px-2 text-rose-600 border-rose-200 hover:bg-rose-50" onClick={() => { setDelRow(t); setDelReason(""); }} data-testid={`txn-delete-${t.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </>
                    ) : (
                      <Button size="sm" variant="outline" className="px-2 gap-1" onClick={() => doUnvoid(t)} data-testid={`txn-restore-${t.id}`}><RotateCcw className="h-4 w-4" /> Restore</Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editRow} onOpenChange={(o) => !o && setEditRow(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Edit transaction</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            {editRow?.type === "consumer_delivery" ? (
              <div className="col-span-2"><Label>Consumer number</Label><Input value={form.consumer_no} onChange={(e) => setForm({ ...form, consumer_no: e.target.value })} className="font-mono" data-testid="edit-txn-consumer" /></div>
            ) : (
              <>
                <div><Label>Customer name</Label><Input value={form.customer_name} onChange={(e) => setForm({ ...form, customer_name: e.target.value })} data-testid="edit-txn-custname" /></div>
                <div><Label>Phone</Label><Input value={form.customer_phone} onChange={(e) => setForm({ ...form, customer_phone: e.target.value.replace(/\D/g, "") })} className="font-mono" /></div>
              </>
            )}
            <div><Label>Cylinder type</Label>
              <Select value={form.cylinder_type} onValueChange={(v) => setForm({ ...form, cylinder_type: v })}>
                <SelectTrigger data-testid="edit-txn-cyl"><SelectValue /></SelectTrigger>
                <SelectContent>{CYLINDERS.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Quantity</Label><Input type="number" value={form.qty} onChange={(e) => setForm({ ...form, qty: e.target.value })} className="font-mono" data-testid="edit-txn-qty" /></div>
            <div><Label>Amount (₹)</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="font-mono" data-testid="edit-txn-amount" /></div>
            <div><Label>Payment mode</Label>
              <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                <SelectTrigger data-testid="edit-txn-mode"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="cash">Cash</SelectItem><SelectItem value="upi">UPI</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label>Empties collected</Label><Input type="number" value={form.empties_collected} onChange={(e) => setForm({ ...form, empties_collected: e.target.value })} className="font-mono" /></div>
            <div className="col-span-2"><Label>Reason for change (required)</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="e.g. driver entered wrong amount" data-testid="edit-txn-reason" /></div>
          </div>
          <DialogFooter><Button onClick={saveEdit} disabled={busy} data-testid="edit-txn-save">Save changes</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete dialog */}
      <Dialog open={!!delRow} onOpenChange={(o) => !o && setDelRow(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-rose-900">Delete transaction</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">This removes it from all totals and reconciliation, but keeps it in the audit log (you can restore it). Enter a reason.</p>
          <Input value={delReason} onChange={(e) => setDelReason(e.target.value)} placeholder="Reason for deleting" className="mt-2" data-testid="delete-txn-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setDelRow(null)}>Cancel</Button>
            <Button variant="destructive" onClick={doVoid} disabled={busy} data-testid="delete-txn-confirm">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* History dialog */}
      <Dialog open={!!histRow} onOpenChange={(o) => !o && setHistRow(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Change history</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-96 overflow-y-auto">
            {(histRow?.history || []).slice().reverse().map((h, i) => (
              <div key={i} className="rounded-lg border p-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="font-medium capitalize">{h.action}</span>
                  <span className="text-xs text-muted-foreground">{fmtDate((h.at || "").slice(0, 10))}</span>
                </div>
                <div className="text-xs text-muted-foreground">by {h.by} — {h.reason}</div>
                {(h.changes || []).map((c, j) => (
                  <div key={j} className="text-xs mt-1"><span className="font-mono">{c.field}</span>: <span className="line-through text-rose-600">{String(c.from)}</span> → <span className="text-emerald-700">{String(c.to)}</span></div>
                ))}
              </div>
            ))}
            {(histRow?.history || []).length === 0 && <p className="text-sm text-muted-foreground">No changes recorded.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
